// SettingsContext.js - Web Version with js-cookie (matching your existing implementation)
import React, { createContext, useState, useContext, useEffect } from "react";
import Cookies from "js-cookie";

const SettingsContext = createContext();
const SettingsUpdateContext = createContext();

// Settings configuration class
export class AppSettings {
  static IS_LOAD_AFTER_SIGN_IN = "isLoadAfterSignIn";
  static INITIAL_ROUTE_NAME = "initialRouteName";
  static SHOW_SAMPLE_PROPERTY_MESSAGE = "showSamplePropertyMessage";
  static SHOW_SAMPLE_BUILDING = "showSampleBuilding";
  static SHOW_SAMPLE_UNIT = "showSampleUnit";
  static SHOULD_NOTIFICATION_PREFERENCES_BE_SET =
    "shouldNotificationPreferencesBeSet";
  static OPEN_REMINDER_IN_WHATSAPP = "openReminderInWhatsApp";
  static ALLOW_HAPTIC_FEEDBACK = "allowHapticFeedback";
  static ASK_FOR_NOTIFICATION_PERMISSION = "askForNotificationPermission";
  static IS_PROPERTIES_SCREEN_MOUNTED = "isPropertiesScreenMounted";
  static SHOW_TAB_NAMES = "showTabNames";
  static SHOW_LOADING_ICON_IN_PROFILE_HOME_SCREEN_FOR_KYC =
    "showLoadingIconInProfileHomeScreenForKYC";
  static LANGUAGE = "language";
  static SORT_REPORTS_BY_LATEST_FIRST = "sortReportsByLatestFirst";
  static SHOW_FUTURE_REPORTS = "showFutureReports";
  static IS_INITIAL_LOAD_PROPERTIES_SCREEN = "isInitialLoadPropertiesScreen";

  static ALL_SETTINGS = [
    AppSettings.IS_LOAD_AFTER_SIGN_IN,
    AppSettings.INITIAL_ROUTE_NAME,
    AppSettings.SHOW_SAMPLE_PROPERTY_MESSAGE,
    AppSettings.SHOW_SAMPLE_BUILDING,
    AppSettings.SHOW_SAMPLE_UNIT,
    AppSettings.SHOULD_NOTIFICATION_PREFERENCES_BE_SET,
    AppSettings.OPEN_REMINDER_IN_WHATSAPP,
    AppSettings.ALLOW_HAPTIC_FEEDBACK,
    AppSettings.ASK_FOR_NOTIFICATION_PERMISSION,
    AppSettings.IS_PROPERTIES_SCREEN_MOUNTED,
    AppSettings.SHOW_TAB_NAMES,
    AppSettings.SHOW_LOADING_ICON_IN_PROFILE_HOME_SCREEN_FOR_KYC,
    AppSettings.LANGUAGE,
    AppSettings.SORT_REPORTS_BY_LATEST_FIRST,
    AppSettings.SHOW_FUTURE_REPORTS,
    AppSettings.IS_INITIAL_LOAD_PROPERTIES_SCREEN,
  ];

  static DEFAULT_SETTINGS = {
    [AppSettings.IS_LOAD_AFTER_SIGN_IN]: true,
    [AppSettings.INITIAL_ROUTE_NAME]: "/dashboard",
    [AppSettings.SHOW_SAMPLE_PROPERTY_MESSAGE]: true,
    [AppSettings.SHOW_SAMPLE_BUILDING]: true,
    [AppSettings.SHOW_SAMPLE_UNIT]: true,
    [AppSettings.SHOULD_NOTIFICATION_PREFERENCES_BE_SET]: true,
    [AppSettings.OPEN_REMINDER_IN_WHATSAPP]: true,
    [AppSettings.ALLOW_HAPTIC_FEEDBACK]: true,
    [AppSettings.ASK_FOR_NOTIFICATION_PERMISSION]: true,
    [AppSettings.IS_PROPERTIES_SCREEN_MOUNTED]: false,
    [AppSettings.SHOW_TAB_NAMES]: true,
    [AppSettings.SHOW_LOADING_ICON_IN_PROFILE_HOME_SCREEN_FOR_KYC]: true,
    [AppSettings.LANGUAGE]: "english",
    [AppSettings.SORT_REPORTS_BY_LATEST_FIRST]: false,
    [AppSettings.SHOW_FUTURE_REPORTS]: true,
    [AppSettings.IS_INITIAL_LOAD_PROPERTIES_SCREEN]: true,
  };

  static RETAINED_SETTINGS = [
    AppSettings.ALLOW_HAPTIC_FEEDBACK,
    AppSettings.ASK_FOR_NOTIFICATION_PERMISSION,
    AppSettings.LANGUAGE,
  ];

  static getDefaultValue(setting) {
    return AppSettings.DEFAULT_SETTINGS?.[setting] ?? false;
  }

  static getValuesToStoreInDB() {
    return [
      AppSettings.ALLOW_HAPTIC_FEEDBACK,
      AppSettings.SHOW_TAB_NAMES,
      AppSettings.LANGUAGE,
      AppSettings.SHOW_FUTURE_REPORTS,
      AppSettings.SORT_REPORTS_BY_LATEST_FIRST,
      AppSettings.OPEN_REMINDER_IN_WHATSAPP,
    ];
  }
}

export function useSettings() {
  return useContext(SettingsContext);
}

export function useUpdateSettings() {
  return useContext(SettingsUpdateContext);
}

export function SettingsProvider({ children }) {
  const settingsKey = "appSettings";
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    getSettingsFromStorage();
  }, []);

  // Function to retrieve settings from cookies using js-cookie (matching your existing implementation)
  const getSettingsFromStorage = () => {
    try {
      const storedSettings = Cookies.get(settingsKey);
      if (storedSettings) {
        setSettings(JSON.parse(storedSettings));
      } else {
        setSettings(AppSettings.DEFAULT_SETTINGS);
        Cookies.set(settingsKey, JSON.stringify(AppSettings.DEFAULT_SETTINGS), {
          path: "/",
        });
      }
    } catch (error) {
      console.error("Error retrieving appSettings from cookies:", error);
      setSettings(AppSettings.DEFAULT_SETTINGS);
    }
  };

  const updateSettings = (newSettings) => {
    if (newSettings === "RESET") {
      console.log("Resetting Settings");
      setSettings((prevSettings) => {
        // Add keys you want to retain after signout here
        const updatedSettings = {};

        AppSettings.RETAINED_SETTINGS.forEach((key) => {
          if (prevSettings && prevSettings.hasOwnProperty(key)) {
            updatedSettings[key] = prevSettings[key];
          }
        });

        // Update cookie storage with the new settings using js-cookie
        Cookies.set(settingsKey, JSON.stringify(updatedSettings), {
          path: "/",
        });
        console.log("Settings reset to", updatedSettings);
        return updatedSettings;
      });
    } else {
      setSettings((prevSettings) => {
        const updatedSettings = { ...prevSettings, ...newSettings };
        const keysToForget = [AppSettings.IS_PROPERTIES_SCREEN_MOUNTED]; // Add keys you want to forget after unmounting of the app here

        const settingsToStore = { ...updatedSettings };
        keysToForget.forEach((key) => {
          if (settingsToStore.hasOwnProperty(key)) {
            delete settingsToStore[key];
          }
        });

        // Update cookie storage with the new settings using js-cookie
        Cookies.set(settingsKey, JSON.stringify(settingsToStore), {
          path: "/",
        });
        return updatedSettings;
      });
    }
  };

  return (
    <SettingsContext.Provider value={settings}>
      <SettingsUpdateContext.Provider value={updateSettings}>
        {children}
      </SettingsUpdateContext.Provider>
    </SettingsContext.Provider>
  );
}
